import type * as THREE from 'three';
import type { DamageFact } from '../../structures/demolition/demolitionDamageLedger';
import type { DamageFieldClock } from './damageFieldTexture';
import {
  BAKE_DOWN_BATCH,
  BAKE_DOWN_TRIGGER,
  DECAL_STAMP_CAPACITY,
  clearDecalStampBuffers,
  createDecalStampBuffers,
  packDecalStamps,
  slicePackedDecalStamps,
  STAMP_ENERGY_CALIBRATION_J,
  type PackedDecalStamps,
} from './damageDecalStamps';

type RestLookup = ReadonlyMap<number, THREE.Vector3> | ((fact: DamageFact) => THREE.Vector3);

export { BAKE_DOWN_BATCH, BAKE_DOWN_TRIGGER };

export interface DecalStampRing {
  packed: PackedDecalStamps;
  bakedDown: number;
  bakedSequences: Set<number>;
}

export interface BakeDownBatch {
  packed: PackedDecalStamps;
  sequences: number[];
}

export function createDecalStampRing(): DecalStampRing {
  return {
    packed: createDecalStampBuffers(),
    bakedDown: 0,
    bakedSequences: new Set<number>(),
  };
}

export function resetDecalStampRing(ring: DecalStampRing): void {
  clearDecalStampBuffers(ring.packed);
  ring.bakedDown = 0;
  ring.bakedSequences.clear();
}

export function unbakedFacts(
  facts: readonly DamageFact[],
  ring: DecalStampRing,
): DamageFact[] {
  if (ring.bakedSequences.size === 0) return facts.slice();
  return facts.filter((fact) => !ring.bakedSequences.has(fact.sequence));
}

/**
 * Oldest-first batch of 24 when unbaked occupancy is at least 40.
 * The same threshold is used for auto and forceBakeDown so a second call
 * after a successful bake-down (live < 40) is a no-op.
 */
export function selectBakeDownBatch(
  facts: readonly DamageFact[],
  ring: DecalStampRing,
  clock: Pick<DamageFieldClock, 'normalize'> = { normalize: (t) => t },
  restPositions: RestLookup = (fact) => fact.position as THREE.Vector3,
  calibrationJ: number = STAMP_ENERGY_CALIBRATION_J,
  mode: 'auto' | 'all' = 'auto',
): BakeDownBatch | null {
  const live = unbakedFacts(facts, ring);
  if (mode === 'auto' && live.length < BAKE_DOWN_TRIGGER) return null;
  if (mode === 'all' && live.length === 0) return null;
  const take = mode === 'all'
    ? Math.min(live.length, DECAL_STAMP_CAPACITY)
    : BAKE_DOWN_BATCH;
  const batchFacts = live.slice(0, take);
  const packed = createDecalStampBuffers();
  packDecalStamps(batchFacts, clock, restPositions, packed, calibrationJ);
  return {
    packed,
    sequences: batchFacts.map((fact) => fact.sequence),
  };
}

export function retireBakeDownBatch(ring: DecalStampRing, batch: BakeDownBatch): void {
  for (const sequence of batch.sequences) {
    if (ring.bakedSequences.has(sequence)) continue;
    ring.bakedSequences.add(sequence);
    ring.bakedDown += 1;
  }
}

export function packLiveDecalStamps(
  ring: DecalStampRing,
  facts: readonly DamageFact[],
  clock: Pick<DamageFieldClock, 'normalize'>,
  restPositions: RestLookup,
  calibrationJ: number = STAMP_ENERGY_CALIBRATION_J,
): PackedDecalStamps {
  packDecalStamps(
    facts,
    clock,
    restPositions,
    ring.packed,
    calibrationJ,
    ring.bakedSequences,
  );
  return ring.packed;
}

/** Slice the oldest `BAKE_DOWN_BATCH` slots already packed in the live ring. */
export function oldestPackedBatch(ring: DecalStampRing): PackedDecalStamps | null {
  if (ring.packed.count < BAKE_DOWN_TRIGGER) return null;
  return slicePackedDecalStamps(ring.packed, 0, BAKE_DOWN_BATCH);
}
